import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, Palette, CreditCard, Crown, UserCircle2, LogOut, Trash2, Check,
  AlertTriangle, Sparkles, Bell, ShieldCheck, FileText, RotateCw, LifeBuoy,
} from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/Toast";
import api, { formatApiError } from "@/lib/api";
import { isIOSNative } from "@/lib/platform";
import { restoreIAP } from "@/lib/iap";
import { getPref, setPref, PREF_CELEBRATIONS } from "@/lib/preferences";

const THEME_OPTIONS = [
  { id: "dark",   name: "Dark",            sub: "Default — luxe black", swatch: ["#000000", "#0c0c0c", "#e8d3b9"] },
  { id: "light",  name: "Light",           sub: "Cream + ink",          swatch: ["#f6f3ee", "#ffffff", "#b08a4d"] },
  { id: "gold",   name: "Black & Gold",    sub: "Maximum chrome",       swatch: ["#050300", "#161002", "#f2c14e"] },
  { id: "mono",   name: "Black & White",   sub: "Pure monochrome",      swatch: ["#000000", "#161616", "#ffffff"] },
  { id: "blue",   name: "Blue Neon",       sub: "Midnight + electric",  swatch: ["#050a14", "#11203d", "#4dc3ff"] },
  { id: "purple", name: "Purple Glow",     sub: "Deep plum + neon",     swatch: ["#0a0414", "#20103a", "#c084ff"] },
];

function Row({ icon: Icon, label, sub, onClick, danger, testId, rightSlot, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || !onClick}
      className="tile"
      data-testid={testId}
      style={{
        flexDirection: "row", alignItems: "center", textAlign: "left", width: "100%",
        opacity: disabled ? 0.55 : 1, cursor: disabled ? "default" : "pointer",
      }}
    >
      <div className="tile-icon"><Icon size={18} strokeWidth={1.7} color={danger ? "var(--danger)" : undefined} /></div>
      <div style={{ flex: 1 }}>
        <div className="h3" style={{ color: danger ? "var(--danger)" : "var(--text)", display: "flex", alignItems: "center", gap: 8 }}>
          {label}
        </div>
        {sub && <div className="tiny" style={{ marginTop: 4, color: "var(--text-faint)" }}>{sub}</div>}
      </div>
      {rightSlot}
    </button>
  );
}

function ThemePicker() {
  const { theme, setTheme } = useTheme();
  return (
    <div className="outfit-section" data-testid="settings-theme-picker">
      <div className="eyebrow" style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
        <Palette size={12} strokeWidth={1.8} /> THEME
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {THEME_OPTIONS.map((opt) => {
          const active = theme === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => setTheme(opt.id)}
              className="tile"
              data-testid={`theme-option-${opt.id}`}
              style={{
                flexDirection: "row", alignItems: "center", textAlign: "left", gap: 14,
                borderColor: active ? "var(--gold-line)" : "var(--line)",
                background: active ? "var(--gold-soft)" : "var(--bg-elev)",
              }}
            >
              <div style={{ display: "flex", gap: -8 }}>
                {opt.swatch.map((c, i) => (
                  <span key={i} style={{
                    width: 26, height: 26, borderRadius: "50%",
                    background: c, border: "1px solid var(--line-strong)",
                    marginLeft: i === 0 ? 0 : -10, boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                  }} />
                ))}
              </div>
              <div style={{ flex: 1 }}>
                <div className="h3" style={{ color: "var(--text)", fontSize: 14 }}>{opt.name}</div>
                <div className="tiny" style={{ marginTop: 3, color: "var(--text-faint)" }}>{opt.sub}</div>
              </div>
              {active && (
                <div className="tile-icon" style={{ background: "var(--gold-soft)", width: 30, height: 30 }}>
                  <Check size={14} color="var(--gold)" strokeWidth={2} />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CelebrationsToggle() {
  const [on, setOn] = useState(() => getPref(PREF_CELEBRATIONS, true));
  const toggle = () => {
    const next = !on;
    setOn(next);
    setPref(PREF_CELEBRATIONS, next);
    if (next) {
      try { navigator.vibrate && navigator.vibrate(20); } catch { /* ignore */ }
    }
  };
  return (
    <div className="outfit-section" data-testid="settings-celebrations">
      <div className="eyebrow" style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
        <Sparkles size={12} strokeWidth={1.8} /> CELEBRATIONS & HAPTICS
      </div>
      <button
        type="button"
        onClick={toggle}
        className="tile"
        data-testid="settings-celebrations-toggle"
        style={{ flexDirection: "row", alignItems: "center", textAlign: "left", width: "100%", gap: 14 }}
      >
        <div className="tile-icon"><Sparkles size={18} strokeWidth={1.7} /></div>
        <div style={{ flex: 1 }}>
          <div className="h3" style={{ color: "var(--text)" }}>Milestone celebrations</div>
          <div className="tiny" style={{ marginTop: 4, color: "var(--text-faint)" }}>
            Confetti + a gentle haptic when you unlock a new Glow Journey milestone.
          </div>
        </div>
        <span
          className="glow-switch"
          data-on={on ? "true" : "false"}
          data-testid="settings-celebrations-switch"
          aria-pressed={on}
          aria-label="Toggle milestone celebrations"
        />
      </button>
    </div>
  );
}

function DeleteAccountModal({ open, onClose, onConfirm, busy }) {
  const [stage, setStage] = useState("initial");         // "initial" | "final"
  const [typed, setTyped] = useState("");
  const REQUIRED_PHRASE = "DELETE";

  // Reset the two-stage state whenever the modal opens/closes so the user
  // never sees a "half-confirmed" flow on next open.
  useEffect(() => {
    if (!open) { setStage("initial"); setTyped(""); }
  }, [open]);

  if (!open) return null;

  const canConfirm = typed.trim().toUpperCase() === REQUIRED_PHRASE;

  return (
    <div className="modal-backdrop" data-testid="delete-modal" onClick={busy ? undefined : onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
        <div className="tile-icon" style={{
          width: 56, height: 56, marginBottom: 16, background: "rgba(255,104,104,0.12)",
          borderColor: "rgba(255,104,104,0.32)",
        }}>
          <AlertTriangle size={20} color="var(--danger)" />
        </div>

        {stage === "initial" ? (
          <>
            <h3 className="h2" style={{ color: "var(--text)", fontSize: 22, marginBottom: 8 }}>
              Delete your account?
            </h3>
            <p className="body-sm" style={{ color: "var(--text-dim)", marginBottom: 8 }}>
              This permanently removes your profile, saved outfits, photos,
              scans, posts, progress, and any other data associated with your
              IX Nova account. This action cannot be undone.
            </p>
            <p className="body-sm" style={{ color: "var(--text-faint)", marginBottom: 18, fontSize: 12 }}>
              If you have an active Premium subscription, it will remain
              associated with your Apple ID and continue billing until you
              cancel it in <strong style={{ color: "var(--text-dim)" }}>iOS Settings → Apple ID → Subscriptions</strong>.
              Cancelling the subscription does <em>not</em> delete your account, and
              deleting the account does <em>not</em> cancel the subscription. Please
              do both if you want to end everything.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                className="btn btn-ghost"
                onClick={onClose}
                data-testid="delete-cancel-btn"
                style={{ flex: 1, justifyContent: "center" }}
              >
                Cancel
              </button>
              <button
                className="btn"
                onClick={() => setStage("final")}
                data-testid="delete-continue-btn"
                style={{ flex: 1, justifyContent: "center", background: "var(--danger)", color: "#fff", border: "1px solid var(--danger)" }}
              >
                Continue
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 className="h2" style={{ color: "var(--text)", fontSize: 22, marginBottom: 8 }}>
              One last check
            </h3>
            <p className="body-sm" style={{ color: "var(--text-dim)", marginBottom: 14 }}>
              To confirm you want to permanently delete your IX Nova account,
              type <strong style={{ color: "var(--text)" }}>{REQUIRED_PHRASE}</strong> in the box below.
            </p>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={REQUIRED_PHRASE}
              disabled={busy}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              data-testid="delete-final-confirm-input"
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.10)",
                background: "rgba(0,0,0,0.30)",
                color: "var(--text)",
                fontSize: 15,
                letterSpacing: "0.14em",
                marginBottom: 16,
                boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", gap: 10 }}>
              <button
                className="btn btn-ghost"
                onClick={() => setStage("initial")}
                disabled={busy}
                data-testid="delete-back-btn"
                style={{ flex: 1, justifyContent: "center" }}
              >
                Back
              </button>
              <button
                className="btn"
                onClick={onConfirm}
                disabled={busy || !canConfirm}
                data-testid="delete-confirm-btn"
                style={{
                  flex: 1, justifyContent: "center",
                  background: canConfirm ? "var(--danger)" : "rgba(255,104,104,0.35)",
                  color: "#fff",
                  border: "1px solid var(--danger)",
                  cursor: canConfirm ? "pointer" : "not-allowed",
                }}
              >
                {busy ? <div className="spinner small" /> : <>Delete forever</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function SettingsScreen() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const toast = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);

  const openBillingPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/billing/portal", {
        return_url: window.location.origin + "/settings",
      });
      if (data?.portal_url) {
        window.location.assign(data.portal_url);
        return;
      }
      toast.show("Couldn't open billing portal. Try again.", "error");
    } catch (err) {
      toast.show(formatApiError(err) || "Couldn't open billing portal.", "error");
    } finally {
      setPortalBusy(false);
    }
  };

  const deleteAccount = async () => {
    setBusy(true);
    try {
      await api.delete("/account");
      toast.show("Account deleted", "success");
      try { localStorage.removeItem("sr_token"); } catch { /* ignore */ }
      // Force-clear in-memory auth state then bounce to welcome.
      try { await logout?.(); } catch { /* ignore */ }
      setConfirmOpen(false);
      navigate("/", { replace: true });
    } catch (err) {
      toast.show(formatApiError(err) || "Couldn't delete account. Try again.", "error");
    } finally { setBusy(false); }
  };

  const premium = !!user?.premium;
  const [restoreBusy, setRestoreBusy] = useState(false);

  const openManageSubscription = () => {
    if (isIOSNative()) {
      // Deep-link into Apple's Subscriptions screen. This is Apple's
      // documented URL scheme for opening the account subscriptions UI
      // from a native app. Approved by App Review 3.1.2.
      window.location.href = "itms-apps://apps.apple.com/account/subscriptions";
      return;
    }
    // On web / Android, route through our premium screen (Stripe portal).
    navigate("/premium");
  };

  const restorePurchases = async () => {
    setRestoreBusy(true);
    try {
      await restoreIAP();
      // Give StoreKit's approved/verified callback a moment to flow through
      // to the backend, then re-read premium status.
      for (let i = 0; i < 6; i++) {
        await new Promise((r) => setTimeout(r, 1000));
        // eslint-disable-next-line no-await-in-loop
        const me = await api.get("/auth/me").then((r) => r.data).catch(() => null);
        if (me?.premium) {
          toast.show("Purchases restored. Premium is active.", "success");
          setRestoreBusy(false);
          return;
        }
      }
      toast.show("No active subscriptions found for this Apple ID.", "info");
    } catch (err) {
      toast.show(formatApiError(err) || "Couldn't restore purchases.", "error");
    } finally {
      setRestoreBusy(false);
    }
  };

  const contactSupport = () => {
    // Open the default mail client with a prefilled subject line — keeps
    // us fully within Apple's guidelines (no external SDKs, no tracking).
    const subject = encodeURIComponent("IX Nova — Support request");
    const body = encodeURIComponent(
      `\n\n---\nEmail: ${user?.email || ""}\nUser ID: ${user?.id || ""}\nApp: IX Nova iOS`
    );
    window.location.href = `mailto:support@ixnovaai.com?subject=${subject}&body=${body}`;
  };

  return (
    <div className="screen with-nav fade-in" data-testid="settings-screen">
      <div className="top-bar">
        <button className="icon-btn" onClick={() => navigate("/profile")} aria-label="Back" data-testid="settings-back">
          <ChevronLeft size={18} />
        </button>
        <div className="logo-mark">SETTINGS</div>
        <span style={{ width: 32 }} />
      </div>

      {/* THEME */}
      <ThemePicker />

      {/* CELEBRATIONS */}
      <CelebrationsToggle />

      {/* REMINDERS — Phase 2 in-app + push notifications hub */}
      <div className="outfit-section" data-testid="settings-reminders-section">
        <div className="eyebrow" style={{ marginBottom: 12 }}>REMINDERS</div>
        <Row
          icon={Bell}
          label="Reminder preferences"
          sub="Water, protein, meals, daily summary, quiet hours"
          onClick={() => navigate("/settings/reminders")}
          testId="settings-row-reminders"
          rightSlot={<ChevronLeft size={18} strokeWidth={1.5} color="var(--text-faint)" style={{ transform: "rotate(180deg)" }} />}
        />
      </div>

      {/* SUBSCRIPTION */}
      <div className="outfit-section">
        <div className="eyebrow" style={{ marginBottom: 12 }}>SUBSCRIPTION</div>
        <Row
          icon={Crown}
          label={premium ? "Manage Premium" : "Upgrade to Premium"}
          sub={premium ? "View plan, cancel, or resume" : "$4.99 / mo — unlimited scans & coaching"}
          onClick={() => navigate("/premium")}
          testId="settings-row-premium"
          rightSlot={<ChevronLeft size={18} strokeWidth={1.5} color="var(--text-faint)" style={{ transform: "rotate(180deg)" }} />}
        />
        <Row
          icon={CreditCard}
          label="Manage Subscription"
          sub={isIOSNative() ? "Open Apple's Subscriptions screen" : "Change or cancel your plan"}
          onClick={openManageSubscription}
          testId="settings-row-manage-subscription"
          rightSlot={<ChevronLeft size={18} strokeWidth={1.5} color="var(--text-faint)" style={{ transform: "rotate(180deg)" }} />}
        />
        {isIOSNative() && (
          <Row
            icon={RotateCw}
            label={restoreBusy ? "Restoring…" : "Restore Purchases"}
            sub="Recover a subscription from your Apple ID"
            onClick={restoreBusy ? undefined : restorePurchases}
            disabled={restoreBusy}
            testId="settings-row-restore-purchases"
            rightSlot={<ChevronLeft size={18} strokeWidth={1.5} color="var(--text-faint)" style={{ transform: "rotate(180deg)" }} />}
          />
        )}
        {premium && !isIOSNative() && (
          <Row
            icon={CreditCard}
            label={portalBusy ? "Opening Stripe…" : "Billing & invoices"}
            sub="Update card, view receipts, cancel subscription"
            onClick={portalBusy ? undefined : openBillingPortal}
            disabled={portalBusy}
            testId="settings-row-billing-portal"
            rightSlot={<ChevronLeft size={18} strokeWidth={1.5} color="var(--text-faint)" style={{ transform: "rotate(180deg)" }} />}
          />
        )}
      </div>

      {/* ACCOUNT */}
      <div className="outfit-section">
        <div className="eyebrow" style={{ marginBottom: 12 }}>ACCOUNT</div>
        <Row
          icon={UserCircle2}
          label="Account information"
          sub={user?.email || "—"}
          testId="settings-row-account-info"
        />
        <Row
          icon={LogOut}
          label="Sign out"
          sub="See you soon"
          onClick={logout}
          testId="settings-row-signout"
        />
        <Row
          icon={Trash2}
          label="Delete account"
          sub="Permanent. All data removed."
          danger
          onClick={() => setConfirmOpen(true)}
          testId="settings-row-delete"
        />
      </div>

      <DeleteAccountModal
        open={confirmOpen}
        busy={busy}
        onClose={() => setConfirmOpen(false)}
        onConfirm={deleteAccount}
      />

      {/* LEGAL */}
      <div className="outfit-section" data-testid="settings-legal-section">
        <div className="eyebrow" style={{ marginBottom: 12 }}>LEGAL & SUPPORT</div>
        <Row
          icon={ShieldCheck}
          label="Privacy Policy"
          sub="How we handle your data"
          onClick={() => navigate("/legal/privacy")}
          testId="settings-row-privacy"
          rightSlot={<ChevronLeft size={18} strokeWidth={1.5} color="var(--text-faint)" style={{ transform: "rotate(180deg)" }} />}
        />
        <Row
          icon={FileText}
          label="Terms of Service"
          sub="Rules of using Nova"
          onClick={() => navigate("/legal/terms")}
          testId="settings-row-terms"
          rightSlot={<ChevronLeft size={18} strokeWidth={1.5} color="var(--text-faint)" style={{ transform: "rotate(180deg)" }} />}
        />
        <Row
          icon={LifeBuoy}
          label="Contact Support"
          sub="Email us for help — we usually reply within 24h"
          onClick={contactSupport}
          testId="settings-row-contact-support"
          rightSlot={<ChevronLeft size={18} strokeWidth={1.5} color="var(--text-faint)" style={{ transform: "rotate(180deg)" }} />}
        />
      </div>

      <div className="tiny" style={{ marginTop: 22, color: "var(--text-faint)", textAlign: "center" }}>
        Nova · v2026.02
      </div>
    </div>
  );
}
